/**
 * Enrolment service — the client surface for one-shot student intake.
 *
 * Wraps the `adminEnrolStudent` callable, which does the whole of onboarding in
 * one call: the login, the `students` record, the enrollment, every class in
 * the series, and the welcome email. See the callable's header for the ordering
 * and what happens when a step fails.
 *
 * The client generates the occurrence instants (it already does, to preview the
 * series) and the server validates them — against the mentor's availability,
 * their existing bookings, and the calendar. One implementation of the weekly
 * recurrence maths, checked on the side that matters.
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from './config';

/** MACHINE_CODE → user-facing message. */
const ERROR_MESSAGES = {
  AUTH_REQUIRED: 'Please sign in and try again.',
  ADMIN_ONLY: 'Only administrators can enrol students.',

  NAME_REQUIRED: "Enter the student's full name.",
  INVALID_EMAIL: 'Enter a valid email address for the student.',
  INVALID_PARENT_EMAIL: "Enter a valid email address for the parent, or leave it blank.",
  INVALID_USERNAME: 'Usernames are 3–30 characters: letters, numbers, dots or underscores.',
  WEAK_PASSWORD: 'Passwords must be at least 8 characters.',
  USERNAME_TAKEN: 'That username is already taken.',
  EMAIL_IN_USE: 'An account already exists for that email address.',
  STUDENT_EXISTS: 'A student record already exists with that email address.',

  COURSE_REQUIRED: 'Choose a course.',
  MENTOR_REQUIRED: 'Choose a mentor.',
  COURSE_NOT_FOUND: 'That course no longer exists — refresh and try again.',
  MENTOR_NOT_FOUND: 'That mentor no longer exists — refresh and try again.',

  TIMEZONE_REQUIRED: 'Choose a timezone for the sessions.',
  NO_SESSIONS: 'Pick at least one weekday and time so there are sessions to book.',
  TOO_MANY_SESSIONS: 'That is more than 60 sessions — book the rest as a second block later.',
  INVALID_SESSION_TIME: 'One of the session times is invalid — adjust the pattern and try again.',
  DUPLICATE_SESSION_TIME: 'Two sessions fall at the same moment — adjust the weekday times.',
  SESSION_IN_PAST: 'The first session is in the past — pick a later start date.',
  OUTSIDE_AVAILABILITY:
    "Some sessions fall outside the mentor's availability. Adjust the times, ask the mentor to extend their schedule, or enrol anyway.",
  MENTOR_DOUBLE_BOOKED: 'The mentor already has a class at one of those times.',

  ENROL_FAILED: 'Enrolment could not be completed. Nothing was created — please try again.',
};

function friendly(err) {
  const machineCode = err?.message;
  const out = new Error(
    ERROR_MESSAGES[machineCode] || machineCode || 'Something went wrong — please try again.'
  );
  out.code = err?.code;
  out.machineCode = machineCode;
  out.details = err?.details;
  return out;
}

const enrolStudentFn = httpsCallable(functions, 'adminEnrolStudent');

export const enrolmentService = {
  /**
   * Enrol a student: account + record + course + schedule + welcome email.
   *
   * @param {object} input
   * @param {string}   input.name           student's full name
   * @param {string}   input.email          also becomes their login
   * @param {string}   [input.username]     optional second login handle
   * @param {string}   [input.password]     omit to have a strong one generated
   * @param {string}   [input.grade]
   * @param {string}   [input.country]
   * @param {string}   input.timezone       labelled tz, e.g. "Asia/Kolkata (IST)"
   * @param {string}   [input.parentName]
   * @param {string}   [input.parentEmail]
   * @param {string}   [input.parentTimezone]
   * @param {string}   input.courseId
   * @param {string}   input.mentorId
   * @param {number[]} input.occurrences    epoch-ms start instants
   * @param {number}   [input.durationMin]
   * @param {string}   [input.title]        session title stem
   * @param {boolean}  [input.force]        book despite availability gaps
   * @returns {Promise<{studentId, enrollmentId, seriesId, uid, email, username,
   *                    password, generatedPassword, sessionCount, firstSessionAt,
   *                    welcomeEmail: {results: Array, setPasswordSent: boolean}}>}
   */
  async enrol(input) {
    try {
      const res = await enrolStudentFn(input);
      return res.data;
    } catch (err) {
      throw friendly(err);
    }
  },
};

export default enrolmentService;
